import { useState, useEffect } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import { EcommerceResponse, IEcommerceAnswerSchema, IEcommerceQuestionSchema } from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { readDimensions } from '../../utils/familyAxes.ts'
import style from './CartRail.module.scss'
const MAX_RAIL_THUMBS = 4

interface ICartRailProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  total: number
  selectedId: string
  onGoToCart: () => void
  onItemSelect: (index: number) => void
  onItemClose: () => void
  onQtyChange: (index: number, qnt: number) => void
}

const CartRail = ({
  schema,
  response,
  total,
  selectedId,
  onGoToCart,
  onItemSelect,
  onItemClose,
  onQtyChange
}: ICartRailProps) => {
  const { t, languageTag } = useSurveyLocale()
  const [open, setOpen] = useState<boolean>(false)
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const sizeOf = (answer: IEcommerceAnswerSchema) => readDimensions(answer)[0]?.value

  const items = schema.answers
    .map(answer => ({ answer, v: response.answers[answer.index].v }))
    .filter(({ answer, v }) => v > 0 && !answer.na)

  const onProductPageOpen = (answer: IEcommerceAnswerSchema) => {
    if (selectedId === answer.id) return
    setOpen(false)
    onItemClose()
    onItemSelect(answer.index)
  }

  // Emptying the cart hides the rail (below) but keeps this mounted, so a stale-open flyout would
  // spring back on refill — close it when the cart empties.
  useEffect(() => {
    if (0 === items.length) setOpen(false)
  }, [items.length])

  if (0 === items.length) return null

  const thumb = (answer: IEcommerceAnswerSchema) =>
    answer.image ? (
      <span
        className={style.thumbImg}
        role="img"
        aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
        style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
      />
    ) : (
      <span className={style.thumbNoImg} />
    )

  return (
    <>
      <aside className={style.rail} data-testid="SW-EC-AddToCartSidebar">
        <button
          type="button"
          className={style.expandButton}
          onClick={() => setOpen(true)}
          aria-label={t('questions', 'ec', 'kroger', 'openCart')}
        >
          <i className="pi pi-chevron-left" />
        </button>
        <div className={style.railThumbs}>
          {items.slice(0, MAX_RAIL_THUMBS).map(({ answer }) => (
            <button
              type="button"
              key={answer.id}
              className={style.railThumb}
              onClick={() => onProductPageOpen(answer)}
              aria-label={t('questions', 'ec', 'kroger', 'openProduct')}
            >
              {thumb(answer)}
            </button>
          ))}
          {items.length > MAX_RAIL_THUMBS && <div className={style.railMore}>+{items.length - MAX_RAIL_THUMBS}</div>}
        </div>
      </aside>

      {open && (
        <aside className={style.panel}>
          <div className={style.panelHeader}>
            <span className={style.panelTitle}>{t('questions', 'ec', 'kroger', 'cart')}</span>
            <button
              type="button"
              className={style.closeButton}
              onClick={() => setOpen(false)}
              aria-label={t('questions', 'ec', 'kroger', 'closeCart')}
            >
              <i className="pi pi-times" />
            </button>
          </div>

          <div className={style.panelBody}>
            <div className={style.sectionTitle}>{t('questions', 'ec', 'kroger', 'pickupItems')}</div>
            {items.map(({ answer, v }) => {
              const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
              const atCap = schema.max_qty_per_product > 0 && v >= schema.max_qty_per_product
              return (
                <div className={style.item} key={answer.id}>
                  <div className={style.itemMain}>
                    <div className={style.itemThumb} onClick={() => onProductPageOpen(answer)}>
                      {thumb(answer)}
                    </div>
                    <div className={style.itemInfo}>
                      <div className={style.itemPriceRow}>
                        {hasDiscount ? (
                          <>
                            <span
                              className={style.itemPriceSale}
                              dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }}
                            />
                            <span
                              className={style.itemPriceOld}
                              dangerouslySetInnerHTML={{ __html: price(answer.price) }}
                            />
                          </>
                        ) : (
                          <span className={style.itemPrice} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
                        )}
                      </div>
                      <div
                        className={style.itemTitle}
                        onClick={() => onProductPageOpen(answer)}
                        dangerouslySetInnerHTML={{ __html: answer.text }}
                      />
                      {/* Size comes from the family axes, never sub_title — same source as the cart line. */}
                      {sizeOf(answer) && <div className={style.itemSize}>{sizeOf(answer)}</div>}
                    </div>
                  </div>
                  <div className={style.itemStepper}>
                    <button
                      type="button"
                      className={style.stepMinus}
                      onClick={() => onQtyChange(answer.index, -1)}
                      aria-label={t('questions', 'ec', 'kroger', 1 === v ? 'remove' : 'decreaseQuantity')}
                    >
                      <i className="pi pi-minus" />
                    </button>
                    <span className={style.stepQty}>{v}</span>
                    <button
                      type="button"
                      className={style.stepPlus}
                      onClick={() => onQtyChange(answer.index, 1)}
                      disabled={atCap}
                      aria-label={t('questions', 'ec', 'kroger', 'increaseQuantity')}
                    >
                      <i className="pi pi-plus" />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <div className={style.panelFooter}>
            <div className={style.estimatedTotal}>
              <span className={style.estimatedTotalLabel}>{t('questions', 'ec', 'kroger', 'estimatedTotal')}</span>
              <span className={style.estimatedTotalValue} dangerouslySetInnerHTML={{ __html: price(total) }} />
            </div>
            <button type="button" className={style.goToCart} onClick={onGoToCart}>
              {t('questions', 'ec', 'kroger', 'goToCart')}
            </button>
          </div>
        </aside>
      )}
    </>
  )
}

export default CartRail
